/**
 * Products domain — cache invalidation registry.
 *
 * Defines which queries every wrapped products mutation should
 * invalidate on success. Consumed by `hooks.ts` via
 * `useInvalidatingMutation`. The compiler enforces that every entry in
 * `ProductsMutationId` has a registry entry, so adding a new mutation
 * without declaring its invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import { PRODUCTS_TABLE_KEY } from '../../constants/tableQueryKeys'
import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'
import {
  getBrandDetailQueryKey,
  getSyndigoSyncStatusQueryKey,
  getVariantDetailQueryKey,
  listBrandsQueryKey,
  listLegacyProductsQueryKey,
  listProductsCatalogQueryKey,
  listVariantImagesQueryKey,
  listVariantsCatalogQueryKey,
} from '../generated/hooks/productsController'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped products mutation. Adding a new
 * mutation requires adding a member here and a corresponding entry in
 * `invalidationRegistry`.
 */
export type ProductsMutationId =
  | 'createVariant'
  | 'updateVariant'
  | 'deleteVariant'
  | 'createBrand'
  | 'updateBrand'
  | 'deleteBrand'
  | 'bulkMatchVariants'
  | 'bulkAssignBrand'
  | 'setVariantMainImage'
  | 'unsetVariantMainImage'
  | 'triggerSyndigoSync'
  | 'exportVariantsCatalog'
  | 'exportProductsCatalog'
  | 'createLegacyProduct'
  | 'updateLegacyProduct'
  | 'deleteLegacyProduct'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys that every product/variant mutation should invalidate.
 *
 * Includes:
 *   - generated query keys (catalog list, variants list, legacy products
 *     list — all the read endpoints exposed by `services/products`)
 *   - legacy string keys still in active use by `api.useGetProducts` /
 *     `api.useGetProductsOptions` / `api.useGetProductVariantsOptions`,
 *     which share cache with other `api.ts` callers.
 */
export function commonProductInvalidations(): QueryKey[] {
  return [
    listProductsCatalogQueryKey(),
    listVariantsCatalogQueryKey(),
    listLegacyProductsQueryKey(),
    ['products'],
    ['productsOptions'],
    ['productVariantsOptions'],
  ]
}

// ── Registry ────────────────────────────────────────────────────────

/**
 * Typed registry mapping every products mutation to the queries it
 * should invalidate on success. Mutations that touch table data also
 * `removes` per-table caches keyed by `PRODUCTS_TABLE_KEY` (substring
 * match covers variants like `settings-products-table`).
 */

export const invalidationRegistry: Record<
  ProductsMutationId,
  InvalidationRule<any>
> = {
  createVariant: {
    invalidates: () => commonProductInvalidations(),
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  updateVariant: {
    invalidates: (variables: { id: number }) => [
      ...commonProductInvalidations(),
      getVariantDetailQueryKey({ id: variables.id }),
      listVariantImagesQueryKey({ variant_id: variables.id }),
    ],
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  deleteVariant: {
    invalidates: () => commonProductInvalidations(),
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  createBrand: {
    invalidates: () => [...commonProductInvalidations(), listBrandsQueryKey()],
  },
  updateBrand: {
    invalidates: (variables: { id: number }) => [
      ...commonProductInvalidations(),
      listBrandsQueryKey(),
      getBrandDetailQueryKey({ id: variables.id }),
    ],
  },
  deleteBrand: {
    invalidates: () => [...commonProductInvalidations(), listBrandsQueryKey()],
  },
  bulkMatchVariants: {
    invalidates: () => commonProductInvalidations(),
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  bulkAssignBrand: {
    invalidates: () => [...commonProductInvalidations(), listBrandsQueryKey()],
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  setVariantMainImage: {
    invalidates: (variables: { variant_id: number }) => [
      listVariantImagesQueryKey({ variant_id: variables.variant_id }),
    ],
  },
  unsetVariantMainImage: {
    invalidates: (variables: { variant_id: number }) => [
      listVariantImagesQueryKey({ variant_id: variables.variant_id }),
    ],
  },
  triggerSyndigoSync: {
    // Sync changes sync status + variant data in-place.
    invalidates: (variables: { variant_id: number }) => [
      getSyndigoSyncStatusQueryKey(),
      listVariantsCatalogQueryKey(),
      getVariantDetailQueryKey({ id: variables.variant_id }),
    ],
  },
  exportVariantsCatalog: {
    // Read-only; wrapped only for registry completeness.
    invalidates: () => [],
  },
  exportProductsCatalog: {
    invalidates: () => [],
  },
  createLegacyProduct: {
    invalidates: () => [
      ...commonProductInvalidations(),
      listLegacyProductsQueryKey(),
    ],
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  updateLegacyProduct: {
    invalidates: () => [
      ...commonProductInvalidations(),
      listLegacyProductsQueryKey(),
    ],
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
  deleteLegacyProduct: {
    invalidates: () => [
      ...commonProductInvalidations(),
      listLegacyProductsQueryKey(),
    ],
    removes: () => [containsKeyFilter(PRODUCTS_TABLE_KEY)],
  },
}
